import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import {
  DataTable,
  EmptyState,
  ErrorState,
  LoadingState,
  MetricCard,
  StatusBadge,
} from '../ui';

/**
 * API-backed learning / continuous improvement panel.
 * Replaces the mock FeedbackLoopSystem in Test Intelligence.
 */
const LearningInsights = () => {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();
  const projectId = getProjectIdFromURL() || selectedProject?.id;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [failedCases, setFailedCases] = useState([]);
  const [healedCases, setHealedCases] = useState([]);
  const [dashboard, setDashboard] = useState(null);

  const navigateTo = useCallback(
    (path) => {
      const workspaceId = getWorkspaceIdFromURL();
      if (workspaceId) {
        navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
      } else {
        navigate(`/dashboard/${path}`);
      }
    },
    [navigate, getWorkspaceIdFromURL, projectId]
  );

  const load = useCallback(async () => {
    if (!projectId) {
      setLoading(false);
      setError('Select a project to load learning insights.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [dupRes, covRes, casesRes, dashRes] = await Promise.all([
        axiosInstance
          .get('/test_case/duplicates', {
            params: { project_id: projectId, threshold: 80 },
          })
          .catch(() => null),
        axiosInstance
          .get('/test_cases/test_coverage', {
            params: { project_id: projectId },
          })
          .catch(() => null),
        axiosInstance
          .get('/test_case/cases_list', {
            params: { project_id: projectId, page: 1, page_size: 100 },
          })
          .catch(() => null),
        axiosInstance
          .get('/projects/dashboard_data', {
            params: { project_id: projectId },
          })
          .catch(() => null),
      ]);

      const groups =
        dupRes?.duplicate_groups ||
        dupRes?.data?.duplicate_groups ||
        [];
      setDuplicates(Array.isArray(groups) ? groups : []);

      const cov = covRes?.data || covRes || null;
      setCoverage(cov);

      const cases = casesRes?.data || [];
      const caseList = Array.isArray(cases) ? cases : [];
      setFailedCases(
        caseList.filter((c) =>
          ['failed', 'fail'].includes(String(c.status || '').toLowerCase())
        )
      );
      setHealedCases(caseList.filter((c) => c.is_healed));

      setDashboard(dashRes || null);

      // Probe healing proposals for up to 5 failed/healed cases
      const probeIds = [
        ...caseList.filter((c) => c.is_healed).slice(0, 3),
        ...caseList
          .filter((c) => ['failed', 'fail'].includes(String(c.status || '').toLowerCase()))
          .slice(0, 3),
      ]
        .map((c) => c.test_case_id || c.id)
        .filter(Boolean)
        .slice(0, 5);

      if (probeIds.length) {
        const proposals = await Promise.all(
          probeIds.map(async (id) => {
            try {
              const res = await axiosInstance.get('/self_healing/get_healing_proposal', {
                params: { test_case_id: id },
              });
              return { test_case_id: id, proposal: res?.data || res };
            } catch {
              return null;
            }
          })
        );
        const withProposal = proposals.filter(Boolean);
        if (withProposal.length) {
          setHealedCases((prev) => {
            const byId = new Map(prev.map((c) => [String(c.test_case_id || c.id), c]));
            withProposal.forEach((p) => {
              const existing = byId.get(String(p.test_case_id));
              if (existing) {
                byId.set(String(p.test_case_id), { ...existing, healing_proposal: p.proposal });
              } else {
                byId.set(String(p.test_case_id), {
                  test_case_id: p.test_case_id,
                  name: `Case ${p.test_case_id}`,
                  healing_proposal: p.proposal,
                });
              }
            });
            return Array.from(byId.values());
          });
        }
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load learning insights');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const coveragePct = useMemo(() => {
    if (!coverage) return dashboard?.metrics?.test_coverage?.current ?? null;
    return (
      coverage.overallCoverage ??
      coverage.overall_coverage ??
      coverage.coverage_percent ??
      coverage.coverage ??
      null
    );
  }, [coverage, dashboard]);

  if (loading) return <LoadingState label="Loading learning signals…" />;
  if (error && !dashboard && !duplicates.length) {
    return <ErrorState description={error} onRetry={load} />;
  }

  return (
    <div className="rt-page" style={{ gap: 14 }}>
      <div className="rt-metric-grid">
        <MetricCard
          label="Pass rate"
          tone="success"
          value={`${dashboard?.metrics?.pass_rate?.current ?? 0}%`}
        />
        <MetricCard
          label="Coverage"
          tone="accent"
          value={coveragePct != null ? `${coveragePct}%` : '—'}
        />
        <MetricCard label="Duplicate groups" tone="violet" value={duplicates.length} />
        <MetricCard label="Failed cases" tone="danger" value={failedCases.length} />
        <MetricCard label="Healed cases" tone="success" value={healedCases.length} />
        <MetricCard
          label="Failed runs"
          tone="danger"
          value={dashboard?.static?.total_test_failed ?? 0}
        />
      </div>

      <div className="rt2-overview-grid">
        <div className="rt2-panel">
          <div className="rt2-panel__header">
            <h2 className="rt2-panel__title">Improvement queue</h2>
            <button type="button" className="rt2-link" onClick={load}>
              Refresh
            </button>
          </div>
          <div className="rt2-panel__body">
            {failedCases.length === 0 && healedCases.length === 0 && (
              <EmptyState
                title="No open learning items"
                description="Failed or healed cases will appear here for follow-up."
              />
            )}
            {failedCases.slice(0, 8).map((c) => (
              <div className="rt2-attention-item" key={`fail-${c.test_case_id || c.id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="rt-status-dot rt-status-dot--danger" />
                  <span>{c.name}</span>
                  <StatusBadge status="failed" />
                </div>
                <button
                  type="button"
                  className="rt2-link"
                  onClick={() =>
                    navigateTo(
                      `test-cases/${c.test_case_id || c.id}?project_id=${projectId}`
                    )
                  }
                >
                  Open case
                </button>
              </div>
            ))}
            {healedCases.slice(0, 8).map((c) => (
              <div className="rt2-attention-item" key={`heal-${c.test_case_id || c.id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="rt-status-dot rt-status-dot--info" />
                  <span>{c.name || `Case ${c.test_case_id}`}</span>
                  <StatusBadge status="healed" />
                </div>
                <button
                  type="button"
                  className="rt2-link"
                  onClick={() =>
                    navigateTo(
                      `test-cases/${c.test_case_id || c.id}?project_id=${projectId}`
                    )
                  }
                >
                  Review heal
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rt2-panel">
          <div className="rt2-panel__header">
            <h2 className="rt2-panel__title">Quality signals</h2>
            <button
              type="button"
              className="rt2-link"
              onClick={() => navigateTo('intelligence')}
            >
              Insights
            </button>
          </div>
          <div className="rt2-panel__body">
            <div className="rt2-attention-item">
              <span>Avg execution time</span>
              <strong>
                {dashboard?.metrics?.avg_execution_time_sec?.current ?? 0}s
              </strong>
            </div>
            <div className="rt2-attention-item">
              <span>Test cases</span>
              <strong>{dashboard?.counts?.test_cases ?? 0}</strong>
            </div>
            <div className="rt2-attention-item">
              <span>Pending / review</span>
              <strong>{dashboard?.static?.total_test_pending ?? 0}</strong>
            </div>
            <div className="rt2-attention-item">
              <span>Requirements coverage gaps</span>
              <strong>
                {coverage?.gapsIdentified ??
                  coverage?.gaps_identified ??
                  coverage?.uncovered_requirements ??
                  '—'}
              </strong>
            </div>
          </div>
        </div>
      </div>

      <div className="rt2-panel">
        <div className="rt2-panel__header">
          <h2 className="rt2-panel__title">Duplicate / redundant cases</h2>
        </div>
        <div className="rt2-panel__body" style={{ padding: 0 }}>
          <DataTable
            columns={[
              {
                key: 'similarity',
                header: 'Similarity',
                render: (row) =>
                  `${Math.round(row.average_similarity ?? row.similarity ?? 0)}%`,
              },
              {
                key: 'cases',
                header: 'Cases',
                render: (row) =>
                  (row.test_cases || [])
                    .map((tc) => tc.name || tc.title || `#${tc.test_case_id || tc.id}`)
                    .join(' · ') || '—',
              },
              {
                key: 'count',
                header: 'Count',
                render: (row) => (row.test_cases || []).length,
              },
            ]}
            rows={duplicates.map((g, idx) => ({ ...g, id: `dup-${idx}` }))}
            rowKey="id"
            emptyTitle="No duplicate groups detected"
            emptyDescription="Raise the similarity threshold in Quality Insights to scan again."
          />
        </div>
      </div>
    </div>
  );
};

export default LearningInsights;
